import React, { useCallback, useState } from "react";
import { Box, Button, InlineGrid, Text, TextField } from "@shopify/polaris";
import { XIcon } from "@shopify/polaris-icons";
import type { CostAdjustmentEditable } from "../../../api_utils/types";
import { formatMoney, getCurrencySymbol } from "../../../helper_functions/utils";
import { calculateCostAdjustmentAmount } from "./calculateCostAdjustmentAmount";

export default function CostAdjustmentRow({
  costAdjustment,
  currency,
  disabled,
  lineTotal,
  onAmountChange,
  onRemove,
}: {
  readonly costAdjustment: CostAdjustmentEditable;
  readonly currency: string;
  readonly disabled: boolean;
  readonly lineTotal: number;
  readonly onAmountChange: (amount: string) => void;
  readonly onRemove: () => void;
}): React.JSX.Element {
  const percentage = costAdjustment.amount_type === "percentage";
  const [amountInput, setAmountInput] = useState<string | null>(null);
  const formattedAmount = percentage || costAdjustment.amount === ""
    ? costAdjustment.amount
    : formatMoney(costAdjustment.amount, undefined, 2);
  const handleAmountChange = useCallback((amount: string): void => {
    const cleanAmount = amount.replace(/[^\d.-]/g, "");
    setAmountInput(cleanAmount);
    onAmountChange(cleanAmount);
  }, [onAmountChange]);
  const handleAmountFocus = useCallback((): void => {
    setAmountInput(formattedAmount);
  }, [formattedAmount]);
  const handleAmountBlur = useCallback((): void => {
    setAmountInput(null);
  }, []);

  return (
    <InlineGrid alignItems="center" columns={percentage ? "1fr 120px 120px auto" : "1fr 120px auto"} gap="400">
      <Text as="span">{costAdjustment.label}</Text>
      <TextField
        align="right"
        autoComplete="off"
        inputMode="decimal"
        label={`${costAdjustment.label} ${percentage ? "percentage" : "amount"}`}
        labelHidden
        onBlur={handleAmountBlur}
        onChange={handleAmountChange}
        onFocus={handleAmountFocus}
        placeholder="0.00"
        prefix={percentage ? undefined : getCurrencySymbol(currency)}
        readOnly={disabled}
        suffix={percentage ? "%" : undefined}
        type={percentage ? "text" : "currency"}
        value={amountInput ?? formattedAmount}
      />
      {percentage ? (
        <Box paddingBlock="150" paddingInline="300">
          <Text alignment="end" as="p">
            {formatMoney(calculateCostAdjustmentAmount(costAdjustment, lineTotal), currency, 2)}
          </Text>
        </Box>
      ) : null}
      <Button
        accessibilityLabel={`Remove ${costAdjustment.label}`}
        disabled={disabled}
        icon={XIcon}
        onClick={onRemove}
        variant="plain"
      />
    </InlineGrid>
  );
}
